// pages/OrderStatusPage.jsx — extracted from App.js (behaviour-preserving split).
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Banknote,
  Camera,
  Check,
  CheckCircle2,
  ClipboardList,
  Timer,
  X,
  X as XIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";
import { requestNotificationPermission, triggerOrderReadyAlert } from "@/lib/notify";
import { formatPrice, getOrderTimeLabel, http } from "@/app/core";
import { EmptyState, MenuLoadingState, saveCart, StatusBadge } from "@/app/fragments";


export function OrderStatusPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmedCelebration, setConfirmedCelebration] = useState(false);
  const prevStatusRef = useRef(null);
  // Track the awaiting-confirmation flag across polls so we can fire a
  // celebration the moment a waiter releases a previously-held order.
  const prevAwaitingRef = useRef(null);
  const [releaseCelebration, setReleaseCelebration] = useState(false);
  // Session 30.2 — Payment return outcome. Cancel/failure means the held
  // order was (or is about to be) deleted server-side; we show a dedicated
  // "order not placed" screen instead of a confusing Order-not-found.
  const [paymentOutcome] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get("payment");
      return p === "cancelled" || p === "failed" ? p : null;
    } catch (_) { return null; }
  });
  const [returnTag] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("tag") || ""; }
    catch (_) { return ""; }
  });

  const fetchOrder = useCallback(async () => {
    try {
      const response = await http.get(`/orders/${orderId}/public`);
      const next = response.data.order;
      setOrder(next);
      // Session 30.30 — first time we see a cash order, nudge the guest to
      // screenshot the page so they have the order number at the counter.
      if (prevStatusRef.current === null && next?.payment_status === "cash_at_counter") {
        toast.info("Screenshot this page to save your order number", {
          description: `Show ${next.order_number || "it"} at the counter when you collect and pay in cash.`,
          duration: 8000,
        });
      }
      // Trigger celebration when status transitions FROM pending → confirmed
      const prev = prevStatusRef.current;
      if (prev && prev !== "confirmed" && next?.status === "confirmed") {
        setConfirmedCelebration(true);
        const businessName = next?.business?.name || "The kitchen";
        triggerOrderReadyAlert(
          "Order confirmed!",
          `${businessName} has confirmed your order. Sit tight — it's being prepared now.`,
        );
        toast.success("Your order has been confirmed!", {
          description: "The kitchen is preparing it now.",
          duration: 5000,
        });
      }
      // Waiter just released a previously-held order — briefly celebrate
      // so the customer sees an obvious "you're good to go" cue.
      const prevAwait = prevAwaitingRef.current;
      const nextAwait = Boolean(next?.awaiting_staff_confirmation);
      if (prevAwait === true && nextAwait === false && !next?.rejected_by_staff) {
        setReleaseCelebration(true);
        const businessName = next?.business?.name || "The team";
        triggerOrderReadyAlert(
          "Order approved!",
          `${businessName} has sent your order to the kitchen.`,
        );
      }
      prevStatusRef.current = next?.status || null;
      prevAwaitingRef.current = nextAwait;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load order.");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (paymentOutcome) return undefined; // cancelled/failed — no order to poll
    fetchOrder();
    const interval = window.setInterval(fetchOrder, 5000);
    // Politely ask for system Notification permission so we can ring even
    // when the tab is in background. Best-effort — silently ignored on iOS.
    requestNotificationPermission().catch(() => {});
    return () => window.clearInterval(interval);
  }, [fetchOrder, paymentOutcome]);

  // Auto-dismiss the big celebration overlay after 4s
  useEffect(() => {
    if (!confirmedCelebration) return;
    const timer = window.setTimeout(() => setConfirmedCelebration(false), 4000);
    return () => window.clearTimeout(timer);
  }, [confirmedCelebration]);

  // Handle payment return URL query params.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentResult = params.get("payment");
    const tagParam = params.get("tag") || "";
    if (paymentResult === "success") {
      toast.success("Payment successful! Your order is being prepared.");
      // Session 30.2 — the cart is only cleared HERE, after the gateway
      // confirmed the payment (it stays intact through the redirect).
      if (tagParam) {
        try { saveCart(tagParam, {}); } catch (_) { /* noop */ }
      }
      // Clean up the URL.
      window.history.replaceState({}, "", window.location.pathname);
    } else if (paymentResult === "cancelled" || paymentResult === "failed") {
      // Session 30.2 — tell the backend to delete the held order so no
      // clutter lingers (idempotent; the webhook may already have).
      http.post(`/orders/${orderId}/abandon`).catch(() => {});
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [orderId]);

  // Auto-dismiss the "released to kitchen" celebration after 3s
  useEffect(() => {
    if (!releaseCelebration) return;
    const timer = window.setTimeout(() => setReleaseCelebration(false), 3000);
    return () => window.clearTimeout(timer);
  }, [releaseCelebration]);


  if (paymentOutcome) {
    // Session 30.2 — Payment cancelled/failed: the order was NOT placed
    // and has been removed. The customer's cart is still saved on their
    // device, so send them straight back to the menu to retry.
    const backToMenuHref = returnTag ? `/menu?tag=${encodeURIComponent(returnTag)}` : "/menu";
    return (
      <main className="min-h-screen bg-[radial-gradient(1200px_circle_at_20%_0%,hsl(var(--accent)/0.18),transparent_55%),radial-gradient(900px_circle_at_90%_10%,hsl(var(--primary)/0.14),transparent_50%)] px-4 py-10 sm:px-6">
        <div className="mx-auto flex w-full max-w-lg flex-col gap-6">
          <Card className="rounded-[28px] border-border bg-card" data-testid="payment-cancelled-card">
            <CardContent className="space-y-4 p-6 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
                <XIcon className="h-7 w-7 text-destructive" />
              </div>
              <h1 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight">
                {paymentOutcome === "failed" ? "Payment failed" : "Payment cancelled"}
              </h1>
              <p className="text-sm text-muted-foreground">
                Your order was <span className="font-semibold text-foreground">not placed</span> and nothing was sent to the kitchen.
                Your cart is still saved — head back to the menu to try again or choose to pay cash at the counter.
              </p>
              <Button asChild className="h-11 w-full rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" data-testid="payment-back-to-menu-btn">
                <a href={backToMenuHref}>Back to the menu</a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <div className="mx-auto min-h-screen w-full max-w-xl px-4 py-6 sm:px-6">
        <MenuLoadingState />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="mx-auto min-h-screen w-full max-w-xl px-4 py-6 sm:px-6">
        <EmptyState icon={ClipboardList} title="Order not found" description="This order is unavailable right now." />
      </div>
    );
  }

  const menuHref = order.tag_code ? `/menu?tag=${encodeURIComponent(order.tag_code)}` : "/menu";

  return (
    <main className="min-h-screen bg-[radial-gradient(1200px_circle_at_20%_0%,hsl(var(--accent)/0.18),transparent_55%),radial-gradient(900px_circle_at_90%_10%,hsl(var(--primary)/0.14),transparent_50%)] px-4 py-6 sm:px-6 sm:py-10">
      {confirmedCelebration ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm animate-in fade-in"
          onClick={() => setConfirmedCelebration(false)}
          data-testid="order-confirmed-overlay"
          role="dialog"
          aria-live="polite"
        >
          <div className="mx-auto w-full max-w-sm rounded-[28px] border border-emerald-300/40 bg-gradient-to-br from-emerald-500 to-emerald-600 p-7 text-center text-white shadow-2xl animate-in zoom-in-90 slide-in-from-bottom-4 duration-300">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white/15 backdrop-blur-sm ring-4 ring-white/25">
              <CheckCircle2 className="h-12 w-12 text-white" strokeWidth={2.5} />
            </div>
            <h2 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight">
              Order confirmed!
            </h2>
            <p className="mt-2 text-sm text-white/90">
              {order.business?.name || "The kitchen"} has confirmed your order.<br/>
              Sit tight — it's being prepared now.
            </p>
            <button
              type="button"
              className="mt-5 rounded-full bg-white/20 px-5 py-2 text-sm font-medium text-white backdrop-blur-sm transition hover:bg-white/30"
              onClick={() => setConfirmedCelebration(false)}
              data-testid="order-confirmed-dismiss-button"
            >
              Got it
            </button>
          </div>
        </div>
      ) : null}
      {releaseCelebration ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm animate-in fade-in"
          onClick={() => setReleaseCelebration(false)}
          data-testid="order-released-overlay"
          role="dialog"
          aria-live="polite"
        >
          <div className="mx-auto w-full max-w-sm rounded-[28px] border border-sky-300/40 bg-gradient-to-br from-sky-500 to-sky-600 p-7 text-center text-white shadow-2xl animate-in zoom-in-90 slide-in-from-bottom-4 duration-300">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-white/15 backdrop-blur-sm ring-4 ring-white/25">
              <Check className="h-12 w-12 text-white" strokeWidth={2.5} />
            </div>
            <h2 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight">
              Order approved!
            </h2>
            <p className="mt-2 text-sm text-white/90">
              A waiter has sent your order through to {order.business?.name || "the kitchen"}.
            </p>
            <button
              type="button"
              className="mt-5 rounded-full bg-white/20 px-5 py-2 text-sm font-medium text-white backdrop-blur-sm transition hover:bg-white/30"
              onClick={() => setReleaseCelebration(false)}
              data-testid="order-released-dismiss-button"
            >
              Got it
            </button>
          </div>
        </div>
      ) : null}
      <div className="mx-auto flex w-full max-w-xl flex-col gap-5">
        {/* Payment status banner */}
        {order.payment_status === "pending" && order.payment_method === "card" && (
          <Card className="rounded-[24px] border-2 border-amber-500/60 bg-amber-50 dark:bg-amber-950/30" data-testid="payment-pending-banner">
            <CardContent className="flex items-start gap-3 p-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white"><Timer className="h-5 w-5" /></div>
              <div className="min-w-0">
                <p className="font-semibold text-amber-700 dark:text-amber-300">Payment pending</p>
                <p className="mt-0.5 text-sm text-muted-foreground">Waiting for payment confirmation. Your order will be sent to the kitchen once the payment is verified.</p>
              </div>
            </CardContent>
          </Card>
        )}
        {order.payment_status === "paid" && (
          <Card className="rounded-[24px] border-2 border-emerald-500/60 bg-emerald-50 dark:bg-emerald-950/30" data-testid="payment-success-banner">
            <CardContent className="flex items-start gap-3 p-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white"><CheckCircle2 className="h-5 w-5" /></div>
              <div className="min-w-0">
                <p className="font-semibold text-emerald-700 dark:text-emerald-300">Payment confirmed</p>
                <p className="mt-0.5 text-sm text-muted-foreground">Your payment was successful. Your order has been sent to the kitchen.</p>
              </div>
            </CardContent>
          </Card>
        )}
        {order.payment_status === "failed" && (
          <Card className="rounded-[24px] border-2 border-red-500/60 bg-red-50 dark:bg-red-950/30" data-testid="payment-failed-banner">
            <CardContent className="flex items-start gap-3 p-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-500 text-white"><X className="h-5 w-5" /></div>
              <div className="min-w-0">
                <p className="font-semibold text-red-700 dark:text-red-300">Payment failed</p>
                <p className="mt-0.5 text-sm text-muted-foreground">Your payment could not be processed. Please speak to staff or try again.</p>
              </div>
            </CardContent>
          </Card>
        )}
        {order.payment_status === "cash_at_counter" && (
          <Card className="overflow-hidden rounded-[24px] border-2 border-sky-500/60 bg-sky-50 dark:bg-sky-950/30" data-testid="payment-cash-banner">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-500 text-white">
                  <Banknote className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sky-700 dark:text-sky-300">Pay cash at the counter</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    Pay <span className="font-semibold text-foreground">{formatPrice(order.total ?? order.subtotal ?? 0)}</span> in cash when you collect your order. Quote your order number at the counter.
                  </p>
                </div>
              </div>
              {/* Session 30.30 — Big order number + screenshot prompt so the
                  guest can prove which order is theirs when collecting. */}
              <div className="rounded-2xl border border-dashed border-sky-500/50 bg-background/70 p-4 text-center" data-testid="cash-order-number-card">
                <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Your order number</p>
                <p className="mt-1 font-['Space_Grotesk'] text-4xl font-bold tracking-tight tabular-nums" data-testid="cash-order-number">{order.order_number || `#${String(order.id || "").slice(-6).toUpperCase()}`}</p>
                <p className="mt-2 inline-flex items-center justify-center gap-1.5 text-sm font-medium text-sky-700 dark:text-sky-300" data-testid="cash-screenshot-hint">
                  <Camera className="h-4 w-4" /> Screenshot this page to save your order number
                </p>
                <p className="mt-1 text-xs text-muted-foreground">Show it at the counter when you collect and pay.</p>
              </div>
            </CardContent>
          </Card>
        )}
        {order.rejected_by_staff ? (
          <Card className="rounded-[24px] border-2 border-red-500/60 bg-red-50 dark:bg-red-950/30" data-testid="order-rejected-banner">
            <CardContent className="flex items-start gap-3 p-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-500 text-white">
                <X className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-red-700 dark:text-red-300">This order was declined</p>
                {order.rejected_reason ? (
                  <p className="mt-1 rounded-xl bg-white/70 px-3 py-2 text-sm text-foreground dark:bg-black/20" data-testid="order-declined-reason">
                    <span className="font-medium">Note from staff:</span> {order.rejected_reason}
                  </p>
                ) : null}
                <p className="mt-0.5 text-sm text-muted-foreground">
                  A waiter declined this order before it reached the kitchen. Please speak to the staff if you'd still like to place it.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : order.awaiting_staff_confirmation ? (
          <Card className="rounded-[24px] border-2 border-sky-500/60 bg-sky-50 dark:bg-sky-950/30" data-testid="order-awaiting-banner">
            <CardContent className="flex items-start gap-3 p-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-500 text-white">
                <Timer className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sky-700 dark:text-sky-300">Waiting for waiter approval</p>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {(order.business?.name || "The restaurant")} reviews QR-scan orders before sending them to the kitchen. Your order will start being prepared as soon as a waiter gives the green light.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          // Gate is OFF → the order was sent straight to the kitchen /
          // bar. Show a positive confirmation banner so the customer
          // knows their order went through without needing to squint at
          // the small "Order sent" badge lower on the card.
          <Card
            className="rounded-[24px] border-2 border-emerald-500/60 bg-emerald-50 dark:bg-emerald-950/30"
            data-testid="order-placed-banner"
          >
            <CardContent className="flex items-start gap-3 p-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                <Check className="h-5 w-5" strokeWidth={3} />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-emerald-700 dark:text-emerald-300">
                  Your order is being prepared
                </p>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  We've received your order and sent it straight to the kitchen and bar. Sit tight — we'll bring it to your table shortly.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        <Card className="rounded-[32px] border-border bg-card shadow-[0_1px_0_rgba(0,0,0,0.04)]">
          <CardHeader className="pb-4">
            {order.rejected_by_staff ? (
              <Badge variant="outline" className="w-fit border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300">Declined</Badge>
            ) : order.awaiting_staff_confirmation ? (
              <Badge className="w-fit bg-sky-500 text-white">Awaiting confirmation</Badge>
            ) : (
              <Badge className="w-fit bg-[hsl(var(--primary))] text-primary-foreground">Order sent</Badge>
            )}
            <CardTitle className="font-['Space_Grotesk'] text-3xl tracking-tight">{order.business?.name}</CardTitle>
            <CardDescription>{getOrderTimeLabel(order.created_at)}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-[24px] border border-border bg-secondary/60 p-4">
                <p className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">Order</p>
                <p className="mt-2 text-2xl font-semibold">{order.order_number}</p>
              </div>
              <div className="rounded-[24px] border border-border bg-secondary/60 p-4">
                <p className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">Table</p>
                <p className="mt-2 text-3xl font-semibold" data-testid="order-success-table-number">{order.table_number}</p>
              </div>
              <div className="rounded-[24px] border border-border bg-secondary/60 p-4">
                <p className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">Status</p>
                <div className="mt-2">
                  <StatusBadge status={order.status} dataTestId="order-success-status-badge" />
                </div>
              </div>
            </div>
            <div className="rounded-[24px] border border-border bg-background p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="font-semibold">Items</p>
                <p className="text-sm text-muted-foreground">{order.total_items} items</p>
              </div>
              <div className="mt-4 space-y-3">
                {(order.items || []).map((item, lineIdx) => (
                  <div key={`${item.name}-${lineIdx}`} className="space-y-1 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-medium">{item.quantity} × {item.name}</p>
                      <p className="font-semibold">{formatPrice(item.line_total)}</p>
                    </div>
                    {Array.isArray(item.selected_sides) && item.selected_sides.length ? (
                      <ul className="ml-5 space-y-0.5 text-xs text-muted-foreground" data-testid="order-line-sides">
                        {item.selected_sides.map((s, idx) => (
                          <li key={`os-${lineIdx}-${idx}`} className="flex items-center gap-1">
                            <span aria-hidden>•</span>
                            <span>{s.name}</span>
                            {s.charged ? (
                              <span className="ml-1 font-medium text-foreground">+{formatPrice(s.price)}</span>
                            ) : (
                              <span className="ml-1 text-emerald-600 dark:text-emerald-400">included</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {Array.isArray(item.prep_selections) && item.prep_selections.length ? (
                      <ul className="ml-5 space-y-0.5 text-xs text-muted-foreground" data-testid="order-line-prep">
                        {item.prep_selections.map((p, idx) => (
                          <li key={`op-${lineIdx}-${idx}`} className="flex items-start gap-1">
                            <span aria-hidden>◦</span>
                            <span><span className="font-medium text-foreground">{p.label}:</span> {p.choice}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {item.note ? (
                      <p className="ml-5 mt-1 text-xs italic text-muted-foreground" data-testid="order-line-note">📝 {item.note}</p>
                    ) : null}
                  </div>
                ))}
              </div>
              <Separator className="my-4" />
              <div className="flex items-center justify-between font-semibold">
                <span>Total</span>
                <span>{formatPrice(order.total)}</span>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Button asChild className="w-full rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90">
              <Link to={menuHref}>Back to menu</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    </main>
  );
}
